import { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { ChevronDown, ChevronRight, AlertCircle, CheckCircle2, MapPin, Download } from 'lucide-react';
import { unknownToLogString } from '@repo/shared-utils';

const REVIEW_STEPS = [
  { id: 1 as const, label: 'Parse summary & warnings', question: 'Did it read my file right?' },
  { id: 2 as const, label: 'Properties & owners', question: 'Are the right people attached to the right properties?' },
  { id: 3 as const, label: 'Units, tenants & leases', question: 'Is my tenant and lease data accurate?' },
  { id: 4 as const, label: 'Final confirmation', question: 'Ready to create account and import.' },
];

const ACCORDION_PAGE_SIZE = 30;
const UNIT_LIST_MAX_HEIGHT = 320;
const UNIT_ROW_ESTIMATE = 88;
const VIRTUALIZE_UNIT_LIST_ABOVE = 25;

type ColumnMappingEntry = { from: string; to: string };

export type ParsedPayload = {
  properties?: Record<string, unknown>[];
  units?: Record<string, unknown>[];
  tenants?: Record<string, unknown>[];
  leases?: Record<string, unknown>[];
  columnMapping?: ColumnMappingEntry[];
  column_mapping?: ColumnMappingEntry[];
  owners?: Array<{ email: string; name?: string; propertyIndex?: number; ownershipPercent?: number }>;
  investors?: Array<{ email: string; name?: string; propertyIndex?: number; ownershipPercent?: number }>;
};

const CANONICAL_LABELS: Record<string, string> = {
  propertyName: 'Property name',
  name: 'Name',
  address: 'Address',
  propertyAddress: 'Property address',
  type: 'Type',
  propertyType: 'Property type',
  units: 'Units',
  unitCount: 'Unit count',
  unitNumber: 'Unit number',
  unit: 'Unit',
  rentAmount: 'Monthly rent',
  rent: 'Rent',
  monthlyRent: 'Monthly rent',
  marketRent: 'Market rent',
  leaseEnd: 'Lease end date',
  leaseStart: 'Lease start date',
  startDate: 'Start date',
  endDate: 'End date',
  tenantName: 'Tenant name',
  tenant: 'Tenant',
  email: 'Email',
  phone: 'Phone',
  deposit: 'Deposit',
  depositAmount: 'Deposit',
  leaseTerm: 'Lease term',
  leaseContractType: 'Lease type',
  hcvFlag: 'HCV / Section 8',
  managementFeeValue: 'Management fee',
  managementFeeType: 'Management fee type',
  repairApprovalThreshold: 'Repair approval threshold',
  emergencyContactName: 'Emergency contact',
  hasPets: 'Has pets',
  hasCoSigner: 'Has co-signer',
  bedrooms: 'Bedrooms',
  bathrooms: 'Bathrooms',
  sqft: 'Square footage',
  status: 'Status',
  balance: 'Balance',
  city: 'City',
  state: 'State',
  zip: 'ZIP',
  sourceId: 'Source ID',
};

type ExceptionItem = { entityType?: string; error?: string; sourceId?: string; payload?: unknown };

/** CSV / migration review display — avoids `[object Object]` when cells contain nested JSON. */
function cellText(v: unknown, maxJson = 160): string {
  if (v == null || v === '') return '';
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v).trim();
  if (typeof v === 'bigint') return v.toString();
  if (v instanceof Date) return v.toISOString();
  if (typeof v === 'object') return unknownToLogString(v, maxJson);
  return String(v);
}

function downloadWarningsCsv(exceptions: ExceptionItem[]): void {
  const headers = ['entityType', 'error', 'sourceId'];
  const rows = exceptions.map((ex) => [
    ex.entityType ?? '',
    (ex.error ?? '').replaceAll('"', '""'),
    ex.sourceId ?? '',
  ]);
  const csv = [headers.join(','), ...rows.map((r) => r.map((c) => `"${c}"`).join(','))].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `migration-warnings-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

type UnitWithDetails = {
  unitIndex: number;
  unit: Record<string, unknown>;
  tenant?: Record<string, unknown>;
  lease?: Record<string, unknown>;
  exceptions: ExceptionItem[];
};

type PropertyGroup = {
  propertyIndex: number;
  property: Record<string, unknown>;
  units: UnitWithDetails[];
  warningCount: number;
};

function formatMoney(v: unknown): string {
  if (v == null || v === '') return '';
  const n = Number(v);
  return Number.isNaN(n) ? '' : `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}/mo`;
}

/** Format repair threshold as dollars with thousand separators. Handles both cents (stored) and dollars (legacy CSV). */
function formatRepairThreshold(value: number): string {
  const dollars = value >= 1000 ? value : value / 100;
  return dollars.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

/** Human-readable location for an exception using payload row (property + unit), fallback to sourceId */
function exceptionLocation(ex: ExceptionItem): string {
  const raw = ex.payload;
  const row = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : undefined;
  if (row) {
    const name = [row.propertyName, row.name, row.propertyAddress, row.address].find((v) => v != null && cellText(v) !== '');
    const unit = [row.unitNumber, row.unit].find((v) => v != null && cellText(v) !== '');
    const nameStr = name == null ? '' : cellText(name);
    const unitStr = unit == null ? '' : cellText(unit);
    if (nameStr && unitStr) return ` — ${nameStr} · Unit ${unitStr}`;
    if (nameStr) return ` — ${nameStr}`;
    if (unitStr) return ` — Unit ${unitStr}`;
  }
  if (ex.sourceId) return ` (source ${ex.sourceId})`;
  return '';
}

function formatDate(v: unknown): string {
  if (v == null || v === '') return '';
  if (v instanceof Date) {
    const d = v;
    return Number.isNaN(d.getTime()) ? cellText(v, 80) : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  if (typeof v === 'object' && v !== null) return cellText(v, 120);
  const s = String(v).trim();
  if (!s) return '';
  const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/;
  const d = isoDateOnly.test(s) ? new Date(s + 'T00:00:00') : new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

function bedBath(unit: Record<string, unknown>): string {
  const bd = unit.bedrooms;
  const bt = unit.bathrooms;
  if (bd != null || bt != null) {
    const bdPart = bd == null ? '?' : typeof bd === 'object' ? cellText(bd, 40) : String(bd);
    const btPart =
      bt == null ? '?' : typeof bt === 'object' ? cellText(bt, 40) : String(Number.isFinite(Number(bt)) ? Number(bt) : bt);
    return `${bdPart}BR/${btPart}BA`;
  }
  return '';
}

function unitSubtitle(unit: Record<string, unknown>): string {
  const type = cellText(unit.type ?? unit.unitType ?? '').toLowerCase();
  const bd = unit.bedrooms;
  const bt = unit.bathrooms;
  const hasBedBath = (bd != null && Number(bd) > 0) || (bt != null && Number(bt) > 0);
  const sqft = unit.sqft == null ? Number.NaN : Number(unit.sqft);
  const hasSqft = !Number.isNaN(sqft) && sqft > 0;

  if (type === 'commercial' || (!hasBedBath && hasSqft)) {
    if (hasSqft) return `${sqft.toLocaleString('en-US')} sqft`;
    return type === 'commercial' ? 'Commercial' : '';
  }
  return bedBath(unit);
}

function tenantDisplayName(t: Record<string, unknown>): string {
  const n = cellText(t.name ?? '');
  if (n) return n;
  const first = cellText(t.firstName ?? '');
  const last = cellText(t.lastName ?? '');
  return [first, last].filter(Boolean).join(' ').trim() || 'Tenant';
}

function tenantDisplayLine(tenant: Record<string, unknown>): { primary: string; showEmailSeparately: boolean } {
  const name = tenantDisplayName(tenant);
  const email = tenant.email == null ? '' : cellText(tenant.email);
  if (name !== 'Tenant') return { primary: name, showEmailSeparately: true };
  if (email) return { primary: email, showEmailSeparately: false };
  return { primary: 'Tenant', showEmailSeparately: false };
}

function VirtualUnitList({
  units,
  editable,
  onUpdateRent,
  onDismissException,
}: {
  units: UnitWithDetails[];
  editable?: boolean;
  onUpdateRent?: (unitIndex: number, value: number) => void;
  onDismissException?: (ex: ExceptionItem) => void;
}) {
  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: units.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => UNIT_ROW_ESTIMATE,
    overscan: 5,
  });
  return (
    <div
      ref={parentRef}
      className="overflow-y-auto overscroll-contain"
      style={{ height: UNIT_LIST_MAX_HEIGHT }}
      role="list"
    >
      <div
        style={{
          height: virtualizer.getTotalSize(),
          position: 'relative',
          width: '100%',
        }}
      >
        {virtualizer.getVirtualItems().map((virtualRow) => {
          const item = units[virtualRow.index];
          return (
            <div
              key={item.unitIndex}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`,
                paddingLeft: 12,
                paddingRight: 12,
                paddingBottom: 8,
              }}
            >
              <UnitRowContent
                unit={item.unit}
                tenant={item.tenant}
                lease={item.lease}
                exceptions={item.exceptions}
                isOnlyUnit={units.length === 1}
                unitIndex={item.unitIndex}
                editable={editable}
                onUpdateRent={editable && onUpdateRent ? (val) => onUpdateRent(item.unitIndex, val) : undefined}
                onDismissException={editable ? onDismissException : undefined}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function UnitRowContent({
  unit,
  tenant,
  lease,
  exceptions: unitEx,
  isOnlyUnit,
  unitIndex,
  editable,
  onUpdateRent,
  onDismissException,
}: {
  unit: Record<string, unknown>;
  tenant?: Record<string, unknown>;
  lease?: Record<string, unknown>;
  exceptions: ExceptionItem[];
  isOnlyUnit?: boolean;
  unitIndex?: number;
  editable?: boolean;
  onUpdateRent?: (value: number) => void;
  onDismissException?: (ex: ExceptionItem) => void;
}) {
  const rawUnitNum = cellText(unit.unitNumber ?? unit.unit ?? '');
  const unitLabel =
    rawUnitNum === ''
      ? isOnlyUnit
        ? 'Primary unit'
        : 'Unit —'
      : `Unit ${rawUnitNum}`;
  const rentVal = unit.rent ?? unit.rentAmount ?? unit.monthlyRent ?? lease?.monthlyRent ?? lease?.rentAmount;
  const rentNum = rentVal != null && rentVal !== '' ? Number(rentVal) : Number.NaN;
  const rentStr = formatMoney(rentVal);
  const rentMissing = tenant && !rentStr;
  const subtitleStr = unitSubtitle(unit);
  const canEditRent = editable && onUpdateRent != null && unitIndex !== undefined;
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 text-sm">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-medium text-slate-900 dark:text-slate-100">{unitLabel}</span>
        {subtitleStr && <span className="text-slate-500 dark:text-slate-400">{subtitleStr}</span>}
        {canEditRent ? (
          <span className="inline-flex items-center gap-1.5">
            <span className="text-slate-600 dark:text-slate-400">Rent:</span>
            <input
              type="number"
              min={0}
              step={50}
              value={Number.isNaN(rentNum) ? '' : rentNum}
              onChange={(e) => {
                const v = e.target.value.trim();
                const n = v === '' ? 0 : Number(v);
                if (!Number.isNaN(n) && n >= 0) onUpdateRent?.(n);
              }}
              className="w-24 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0.5 text-slate-900 dark:text-slate-100"
              placeholder="0"
            />
            <span className="text-slate-500 dark:text-slate-400">/mo</span>
          </span>
        ) : rentStr ? (
          <span className="text-slate-700 dark:text-slate-300">{rentStr}</span>
        ) : rentMissing ? (
          <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
            <AlertCircle size={14} /> rent missing
          </span>
        ) : null}
      </div>
      {unitEx.length > 0 && (
        <div className="mt-1.5 space-y-0.5">
          {unitEx.map((ex, i) => (
            <div key={i} className="flex items-start gap-1.5 text-amber-700 dark:text-amber-300">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <span className="min-w-0">{ex.error}{exceptionLocation(ex)}</span>
              {editable && onDismissException && (
                <button
                  type="button"
                  onClick={() => onDismissException(ex)}
                  className="shrink-0 text-xs font-medium text-slate-500 hover:text-slate-700 dark:hover:text-slate-200"
                >
                  Dismiss
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {tenant ? (
        <div className="mt-2 text-slate-600 dark:text-slate-400">
          {(() => {
            const { primary, showEmailSeparately } = tenantDisplayLine(tenant);
            return (
              <>
                {primary}
                {showEmailSeparately && tenant.email && ` · ${cellText(tenant.email)}`}
              </>
            );
          })()}
          {lease != null && (tenant.leaseStart != null || tenant.leaseEnd != null) && (
            <>
              {' '}
              · Lease: {formatDate(tenant.leaseStart ?? lease.startDate ?? '')} –{' '}
              {formatDate(tenant.leaseEnd ?? lease.endDate ?? '')}
            </>
          )}
          {lease != null &&
            cellText(
              lease.leaseContractType ?? (lease.addons as Record<string, unknown>)?.leaseContractType ?? ''
            ).trim() !== '' && (
            <span className="ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-xs font-medium bg-slate-200 dark:bg-slate-600 text-slate-700 dark:text-slate-300">
              {cellText(lease.leaseContractType ?? (lease.addons as Record<string, unknown>)?.leaseContractType ?? '')}
            </span>
          )}
          {!unitEx.length && <span className="ml-1 text-emerald-600 dark:text-emerald-400">✓</span>}
        </div>
      ) : (
        <div className="mt-2 text-slate-500 dark:text-slate-400">Vacant</div>
      )}
    </div>
  );
}

function buildPropertyGroups(payload: ParsedPayload, exceptions: ExceptionItem[]): PropertyGroup[] {
  const properties = payload.properties ?? [];
  const units = payload.units ?? [];
  const tenants = payload.tenants ?? [];
  const leases = payload.leases ?? [];

  const exceptionsByUnitIndex: Record<number, ExceptionItem[]> = {};
  for (const ex of exceptions) {
    const sid = ex.sourceId;
    if (sid !== undefined && sid !== '') {
      const idx = Number.parseInt(sid, 10);
      if (!Number.isNaN(idx)) {
        if (!exceptionsByUnitIndex[idx]) exceptionsByUnitIndex[idx] = [];
        exceptionsByUnitIndex[idx].push(ex);
      }
    }
  }

  const tenantByKey = new Map<string, Record<string, unknown>>();
  for (let i = 0; i < tenants.length; i++) {
    const t = tenants[i];
    const pi = Number(t.propertyIndex ?? 0);
    const ui = t.unitIndex;
    tenantByKey.set(`${pi}-${ui}`, t);
  }

  const leaseByKey = new Map<string, Record<string, unknown>>();
  for (let i = 0; i < leases.length; i++) {
    const l = leases[i];
    const pi = Number(l.propertyIndex ?? 0);
    const ui = l.unitIndex;
    leaseByKey.set(`${pi}-${ui}`, l);
  }

  const unitsByProperty: Record<number, { unitIndex: number; unit: Record<string, unknown> }[]> = {};
  for (let i = 0; i < units.length; i++) {
    const u = units[i];
    const pi = Number(u.propertyIndex ?? 0);
    if (!unitsByProperty[pi]) unitsByProperty[pi] = [];
    unitsByProperty[pi].push({ unitIndex: i, unit: u });
  }

  const groups: PropertyGroup[] = [];
  for (let p = 0; p < properties.length; p++) {
    const property = properties[p];
    const unitEntries = unitsByProperty[p] ?? [];
    const unitDetails: UnitWithDetails[] = unitEntries.map(({ unitIndex, unit }) => {
      const key = `${p}-${unitIndex}`;
      const tenant = tenantByKey.get(key);
      const lease = leaseByKey.get(key);
      const exceptionsForUnit = exceptionsByUnitIndex[unitIndex] ?? [];
      return { unitIndex, unit, tenant, lease, exceptions: exceptionsForUnit };
    });
    const warningCount = unitDetails.reduce((sum, u) => sum + u.exceptions.length, 0);
    groups.push({propertyIndex: p, units: unitDetails, property, warningCount});
  }
  return groups;
}

type Props = {
  payload: ParsedPayload;
  exceptions: ExceptionItem[];
  setFormValue?: (name: string, value: unknown) => void;
  /** When set, review edits (payload, exceptions) are mutable and persisted via PUT. */
  onReviewDataChange?: (payload: ParsedPayload, exceptions: ExceptionItem[]) => void;
  uploadToken?: string;
};

type ReviewStep = 1 | 2 | 3 | 4;

export function MigrationReviewContent({ payload, exceptions, setFormValue, onReviewDataChange, uploadToken }: Props) {
  const editable = Boolean(onReviewDataChange && uploadToken);
  const [localPayload, setLocalPayload] = useState<ParsedPayload>(payload);
  const [localExceptions, setLocalExceptions] = useState<ExceptionItem[]>(exceptions);
  useEffect(() => {
    queueMicrotask(() => {
      setLocalPayload(payload);
      setLocalExceptions(exceptions);
    });
  }, [payload, exceptions]);

  const effectivePayload = editable ? localPayload : payload;
  const effectiveExceptions = editable ? localExceptions : exceptions;

  const [reviewStep, setReviewStep] = useState<ReviewStep>(1);
  const [warningsOnly, setWarningsOnly] = useState(false);
  const [expandedPropertyIndices, setExpandedPropertyIndices] = useState<Set<number>>(new Set());
  const [accordionPage, setAccordionPage] = useState(0);
  const [confirmedFlagged, setConfirmedFlagged] = useState(false);
  const firstWarningRef = useRef<HTMLDivElement>(null);
  const step3InitializedRef = useRef(false);

  const propertyGroups = useMemo(
    () => buildPropertyGroups(effectivePayload, effectiveExceptions),
    [effectivePayload, effectiveExceptions]
  );

  const filteredGroups = useMemo(() => {
    if (!warningsOnly) return propertyGroups;
    return propertyGroups.filter((g) => g.warningCount > 0);
  }, [propertyGroups, warningsOnly]);

  const totalProps = effectivePayload.properties?.length ?? 0;
  const totalUnits = effectivePayload.units?.length ?? 0;
  const totalTenants = effectivePayload.tenants?.length ?? 0;
  const totalLeases = effectivePayload.leases?.length ?? 0;
  const totalWarnings = effectiveExceptions.length;
  const hasWarnings = totalWarnings > 0;

  const handleDismissException = useCallback((index: number) => {
    if (!editable || !onReviewDataChange) return;
    const next = localExceptions.filter((_, i) => i !== index);
    setLocalExceptions(next);
    onReviewDataChange(localPayload, next);
  }, [editable, onReviewDataChange, localPayload, localExceptions]);

  const handleDismissExceptionByRef = useCallback((ex: ExceptionItem) => {
    if (!editable) return;
    const i = localExceptions.findIndex(
      (e) => e === ex || (e.sourceId === ex.sourceId && e.error === ex.error && e.entityType === ex.entityType)
    );
    if (i >= 0) handleDismissException(i);
  }, [editable, localExceptions, handleDismissException]);

  const handleUpdateUnitRent = useCallback((unitIndex: number, value: number) => {
    if (!editable || !onReviewDataChange || !localPayload) return;
    const units = [...(localPayload.units ?? [])];
    const u = units[unitIndex] as Record<string, unknown> | undefined;
    if (!u) return;
    units[unitIndex] = { ...u, rent: value, rentAmount: value };
    const leases = [...(localPayload.leases ?? [])];
    const leaseIdx = leases.findIndex((l) => Number((l).unitIndex) === unitIndex);
    if (leaseIdx >= 0) {
      const le = leases[leaseIdx];
      leases[leaseIdx] = { ...le, monthlyRent: value, rentAmount: value };
    }
    const next = { ...localPayload, units, leases };
    setLocalPayload(next);
    onReviewDataChange(next, localExceptions);
  }, [editable, onReviewDataChange, localPayload, localExceptions]);

  const migrationSummary = useMemo(() => {
    const leases = (effectivePayload.leases ?? []);
    const tenants = (effectivePayload.tenants ?? []);
    const properties = (effectivePayload.properties ?? []);
    const units = (effectivePayload.units ?? []);

    let depositCount = 0;
    let depositTotal = 0;
    for (const lease of leases) {
      const d = lease.deposit == null ? 0 : Number(lease.deposit);
      if (d > 0) {
        depositCount += 1;
        depositTotal += d;
      }
    }

    let hcvCount = 0;
    for (const t of tenants) {
      const flag = t.hcvFlag;
      const s = flag == null ? '' : cellText(flag).trim().toLowerCase();
      if (s === 'true' || s === 'yes' || s === '1' || flag === true || flag === 1) {
        hcvCount += 1;
      }
    }

    let operationsSetupCount = 0;
    let operationsConfiguredCount = 0;
    for (const p of properties) {
      const hasRepair = p.repairApprovalThreshold != null && Number(p.repairApprovalThreshold) >= 0;
      const hasMarkup = (p.maintenanceMarkupType != null && cellText(p.maintenanceMarkupType).trim() !== '') ||
        (p.maintenanceMarkupValue != null && Number(p.maintenanceMarkupValue) >= 0);
      const hasMgmtFee = (p.managementFeeType != null && cellText(p.managementFeeType).trim() !== '') ||
        (p.managementFeeValue != null && Number(p.managementFeeValue) >= 0);
      if (!hasRepair && !hasMarkup) operationsSetupCount += 1;
      if (hasRepair && (hasMarkup || hasMgmtFee)) operationsConfiguredCount += 1;
    }

    let commercialCount = 0;
    for (const u of units) {
      const type = cellText(u.type ?? u.unitType ?? '').trim().toLowerCase();
      if (type === 'commercial') commercialCount += 1;
    }

    let monthToMonthCount = 0;
    for (const lease of leases) {
      const ct = cellText(lease.leaseContractType ?? (lease.addons as Record<string, unknown>)?.leaseContractType ?? '').trim().toLowerCase();
      if (ct === 'month-to-month' || ct === 'month to month' || ct === 'mtm') monthToMonthCount += 1;
    }

    const owners = (effectivePayload).owners ?? [];
    const investors = (effectivePayload).investors ?? [];
    const ownerCount = new Set(owners.map((o) => o.email?.trim().toLowerCase()).filter(Boolean)).size;
    const investorCount = new Set(investors.map((i) => i.email?.trim().toLowerCase()).filter(Boolean)).size;

    let portalInviteCount = 0;
    for (const t of tenants) {
      const email = t.email == null ? '' : cellText(t.email);
      if (email) portalInviteCount += 1;
    }

    return {
      depositCount,
      depositTotal,
      hcvCount,
      operationsSetupCount,
      operationsConfiguredCount,
      commercialCount,
      monthToMonthCount,
      ownerCount,
      investorCount,
      portalInviteCount,
    };
  }, [effectivePayload]);

  const formatDepositTotal = (n: number): string =>
    n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

  const paginatedGroups = useMemo(() => {
    const start = accordionPage * ACCORDION_PAGE_SIZE;
    return filteredGroups.slice(start, start + ACCORDION_PAGE_SIZE);
  }, [filteredGroups, accordionPage]);

  const totalPages = Math.ceil(filteredGroups.length / ACCORDION_PAGE_SIZE);
  const showPagination = filteredGroups.length > ACCORDION_PAGE_SIZE;

  const columnMapping = (effectivePayload.columnMapping ?? effectivePayload.column_mapping ?? []);
  const hasColumnMapping = columnMapping.length > 0;

  const jumpToWarnings = useCallback(() => {
    setWarningsOnly(true);
    setAccordionPage(0);
    const first = propertyGroups.find((g) => g.warningCount > 0);
    if (first != null) setExpandedPropertyIndices((prev) => new Set(prev).add(first.propertyIndex));
    setTimeout(() => firstWarningRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
  }, [propertyGroups]);

  const toggleExpand = useCallback((propertyIndex: number) => {
    setExpandedPropertyIndices((prev) => {
      const next = new Set(prev);
      if (next.has(propertyIndex)) next.delete(propertyIndex);
      else next.add(propertyIndex);
      return next;
    });
  }, []);

  useEffect(() => {
    setFormValue?.('migrationReviewStep', reviewStep);
  }, [reviewStep, setFormValue]);

  useEffect(() => {
    setFormValue?.('confirmed', confirmedFlagged);
  }, [confirmedFlagged, setFormValue]);

  useEffect(() => {
    if (reviewStep === 3 && !step3InitializedRef.current && propertyGroups.length > 0) {
      step3InitializedRef.current = true;
      const withWarnings = propertyGroups.filter((g) => g.warningCount > 0).map((g) => g.propertyIndex);
      queueMicrotask(() => {
        setWarningsOnly(true);
        setAccordionPage(0);
        if (withWarnings.length > 0) {
          setExpandedPropertyIndices((prev) => new Set([...prev, ...withWarnings]));
        }
      });
    }
  }, [reviewStep, propertyGroups]);

  const handleContinueToAccount = useCallback(() => {
    (document.getElementById('onboarding-form-migration-review') as HTMLFormElement | null)?.requestSubmit();
  }, []);

  const totalDeposits = migrationSummary.depositCount;
  const ownerCount = migrationSummary.ownerCount ?? 0;
  const investorCount = migrationSummary.investorCount ?? 0;
  const portalInviteCount = migrationSummary.portalInviteCount ?? totalTenants;

  return (
    <div className="space-y-6">
      <input type="hidden" name="migrationReviewStep" value={reviewStep} />
      {/* Stepper */}
      <div className="flex items-center gap-2 flex-wrap">
        {REVIEW_STEPS.map((step, idx) => {
          const isActive = reviewStep === step.id;
          const isDone = reviewStep > step.id;
          return (
            <div key={step.id} className="flex items-center gap-2">
              <div
                className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  isDone ? 'bg-emerald-600 text-white' : isActive ? 'bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 ring-2 ring-slate-400' : 'bg-slate-200 dark:bg-slate-700 text-slate-500'
                }`}
              >
                {isDone ? <CheckCircle2 size={16} /> : step.id}
              </div>
              <span className={`text-sm font-medium ${isActive ? 'text-slate-900 dark:text-slate-100' : 'text-slate-500 dark:text-slate-400'}`}>
                {step.label}
              </span>
              {idx < REVIEW_STEPS.length - 1 && <ChevronRight size={14} className="text-slate-300 dark:text-slate-600 shrink-0" />}
            </div>
          );
        })}
      </div>

      <p className="text-sm text-slate-600 dark:text-slate-400">
        {REVIEW_STEPS.find((s) => s.id === reviewStep)?.question}
      </p>

      {/* Step 1: Parse summary & warnings */}
      {reviewStep === 1 && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-4">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalProps}</strong> properties</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalUnits}</strong> units</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalTenants}</strong> tenants</span>
              <span className="text-slate-400">·</span>
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalLeases}</strong> leases</span>
              {totalWarnings > 0 && (
                <>
                  <span className="text-slate-400">·</span>
                  <span className="text-amber-600 dark:text-amber-400"><strong>{totalWarnings}</strong> warnings</span>
                </>
              )}
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-4 space-y-2">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Migration summary</p>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1">
              {migrationSummary.depositCount > 0 && (
                <li><strong>{migrationSummary.depositCount}</strong> security deposit{migrationSummary.depositCount === 1 ? '' : 's'} totaling {formatDepositTotal(migrationSummary.depositTotal)} will be recorded.</li>
              )}
              {ownerCount + investorCount > 0 && (
                <li><strong>{ownerCount}</strong> owner{(ownerCount) === 1 ? '' : 's'} and <strong>{investorCount}</strong> investor{(investorCount) === 1 ? '' : 's'} will be created and linked to <strong>{totalProps}</strong> propert{totalProps === 1 ? 'y' : 'ies'}.</li>
              )}
              {migrationSummary.operationsConfiguredCount > 0 && (
                <li><strong>{migrationSummary.operationsConfiguredCount}</strong> propert{(migrationSummary.operationsConfiguredCount) === 1 ? 'y' : 'ies'} with management fees and repair thresholds configured — review in Step 2.</li>
              )}
              {migrationSummary.operationsSetupCount > 0 && (
                <li><strong>{migrationSummary.operationsSetupCount}</strong> propert{migrationSummary.operationsSetupCount === 1 ? 'y' : 'ies'} need{migrationSummary.operationsSetupCount === 1 ? 's' : ''} management fee and repair threshold confirmation.</li>
              )}
              {portalInviteCount > 0 && (
                <li><strong>{portalInviteCount}</strong> tenant{portalInviteCount === 1 ? '' : 's'} will receive portal invite emails after signup.</li>
              )}
              {migrationSummary.hcvCount > 0 && (
                <li><strong>{migrationSummary.hcvCount}</strong> tenant{migrationSummary.hcvCount === 1 ? '' : 's'} flagged as HCV participant{migrationSummary.hcvCount === 1 ? '' : 's'}.</li>
              )}
              {migrationSummary.commercialCount > 0 && (
                <li><strong>{migrationSummary.commercialCount}</strong> commercial unit{migrationSummary.commercialCount === 1 ? '' : 's'} — CAM setup may be required.</li>
              )}
              {migrationSummary.monthToMonthCount > 0 && (
                <li><strong>{migrationSummary.monthToMonthCount}</strong> month-to-month lease{migrationSummary.monthToMonthCount === 1 ? '' : 's'} (renewal workflow may apply).</li>
              )}
            </ul>
          </div>

          {hasColumnMapping && (
            <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 px-3 py-2">
              <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-1.5">We mapped your columns:</p>
              <p className="text-xs text-slate-600 dark:text-slate-400">
                {columnMapping.map(({ from, to }) => `${from} → ${CANONICAL_LABELS[to] ?? to}`).join(' · ')}
              </p>
            </div>
          )}

          {effectiveExceptions.length > 0 && (
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-4">
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Warnings</p>
                <button
                  type="button"
                  onClick={() => downloadWarningsCsv(effectiveExceptions)}
                  className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100"
                >
                  <Download size={14} /> Download warnings
                </button>
              </div>
              <ul className="space-y-1.5 max-h-48 overflow-y-auto">
                {effectiveExceptions.map((ex, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-amber-800 dark:text-amber-200">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    <span className="min-w-0">{ex.entityType && `${ex.entityType}: `}{ex.error}{exceptionLocation(ex)}</span>
                    {editable && (
                      <button
                        type="button"
                        onClick={() => handleDismissException(i)}
                        className="shrink-0 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                      >
                        Dismiss
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setReviewStep(2)} className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Properties & owners */}
      {reviewStep === 2 && (
        <div className="space-y-4">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-4">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">Properties & ownership</p>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(effectivePayload.properties ?? []).map((p, idx) => {
                const name = cellText(p.name ?? p.propertyName ?? 'Unnamed');
                const address = cellText(p.address ?? p.propertyAddress ?? '');
                const type = cellText(p.type ?? p.propertyType ?? '');
                const unitCount = Number(p.units ?? p.unitCount ?? 1);
                const repairCents = p.repairApprovalThreshold == null ? null : Number(p.repairApprovalThreshold);
                const repairStr = repairCents == null ? null : formatRepairThreshold(repairCents);
                const mgmtType = p.managementFeeType == null ? '' : cellText(p.managementFeeType);
                const mgmtVal = p.managementFeeValue == null ? null : Number(p.managementFeeValue);
                const isPercentFee =
                  /percent/i.test(mgmtType) ||
                  mgmtType === 'PercentOfRent' ||
                  mgmtType === 'PERCENT_OF_COLLECTED' ||
                  mgmtType === 'PERCENT_OF_BILLED';
                const mgmtDisplay = mgmtVal == null ? '' : (isPercentFee ? `${mgmtVal}%` : `$${mgmtVal}`);
                const propertyOwners = (effectivePayload.owners ?? []).filter((o) => o.propertyIndex === idx);
                const propertyInvestors = (effectivePayload.investors ?? []).filter((i) => i.propertyIndex === idx);
                const dedupeByEmail = <T extends { email: string }>(arr: T[]): T[] => {
                  const seen = new Set<string>();
                  return arr.filter((x) => {
                    const key = x.email.trim().toLowerCase();
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                };
                const ownersList = dedupeByEmail(propertyOwners);
                const investorsList = dedupeByEmail(propertyInvestors);
                return (
                  <div key={idx} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 p-3 text-sm">
                    <div className="font-medium text-slate-900 dark:text-slate-100">{name || 'Unnamed Property'}</div>
                    {address && <div className="text-slate-500 dark:text-slate-400">{address}</div>}
                    <div className="mt-1.5 flex flex-wrap gap-2 text-slate-600 dark:text-slate-400">
                      {type && <span>{type}</span>}
                      <span>{unitCount} unit{unitCount === 1 ? '' : 's'}</span>
                      {repairStr != null && <span>Repair threshold: {repairStr}</span>}
                      {(mgmtType || mgmtDisplay) && <span>Mgmt: {mgmtType || ''} {mgmtDisplay}</span>}
                    </div>
                    {(ownersList.length > 0 || investorsList.length > 0) && (
                      <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700 space-y-1">
                        {ownersList.map((o, i) => (
                          <div key={`o-${i}`} className="text-slate-600 dark:text-slate-400">
                            Owner: {o.name || o.email}{o.name && ` (${o.email})`} {o.ownershipPercent == null ? '' : `— ${o.ownershipPercent}%`}
                          </div>
                        ))}
                        {investorsList.map((inv, i) => (
                          <div key={`i-${i}`} className="text-slate-600 dark:text-slate-400">
                            Investor: {inv.name || inv.email}{inv.name && ` (${inv.email})`} {inv.ownershipPercent == null ? '' : `— ${inv.ownershipPercent}%`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex justify-between gap-2">
            <button type="button" onClick={() => setReviewStep(1)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium">
              Back
            </button>
            <button type="button" onClick={() => setReviewStep(3)} className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium">
              Next
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Units, tenants & leases (accordion) */}
      {reviewStep === 3 && (
        <div className="space-y-4">
          <div className="sticky top-0 z-10 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/95 backdrop-blur py-3 px-4 shadow-sm">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalProps}</strong> properties</span>
              <span className="text-slate-500 dark:text-slate-400">·</span>
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalUnits}</strong> units</span>
              <span className="text-slate-500 dark:text-slate-400">·</span>
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalTenants}</strong> tenants</span>
              <span className="text-slate-500 dark:text-slate-400">·</span>
              <span className="text-slate-700 dark:text-slate-300"><strong>{totalLeases}</strong> leases</span>
              {totalWarnings > 0 && (
                <>
                  <span className="text-slate-500 dark:text-slate-400">·</span>
                  <span className="text-amber-600 dark:text-amber-400"><strong>{totalWarnings}</strong> warnings</span>
                </>
              )}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-3">
              {hasWarnings && (
                <button type="button" onClick={jumpToWarnings} className="text-xs font-medium text-amber-600 dark:text-amber-400 hover:underline">
                  Jump to warnings
                </button>
              )}
              <label className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={warningsOnly}
                  onChange={(e) => { setWarningsOnly(e.target.checked); if (!e.target.checked) setAccordionPage(0); }}
                  className="rounded border-slate-300 dark:border-slate-600"
                />
                Warnings only
              </label>
            </div>
          </div>

          <div className="space-y-2" ref={hasWarnings && warningsOnly ? firstWarningRef : undefined}>
            {paginatedGroups.map((group) => {
          const isExpanded = expandedPropertyIndices.has(group.propertyIndex);
          const name = cellText(group.property.name ?? group.property.propertyName ?? 'Unnamed');
          const address = cellText(
            group.property.address ?? (group.property as { propertyAddress?: unknown }).propertyAddress ?? ''
          );
          const unitCount = group.units.length;
          const tenantCount = group.units.filter((u) => u.tenant).length;
          const hasIssues = group.warningCount > 0;

          return (
            <div
              key={group.propertyIndex}
              className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 overflow-hidden"
            >
              <button
                type="button"
                onClick={() => toggleExpand(group.propertyIndex)}
                className="w-full flex items-center gap-3 p-4 text-left hover:bg-slate-100/80 dark:hover:bg-slate-800/50 transition"
              >
                {isExpanded ? (
                  <ChevronDown size={18} className="text-slate-500 shrink-0" />
                ) : (
                  <ChevronRight size={18} className="text-slate-500 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-slate-900 dark:text-slate-100 truncate">{name || 'Unnamed Property'}</div>
                  {address && (
                    <div className="flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400 truncate">
                      <MapPin size={12} className="shrink-0" />
                      {address}
                    </div>
                  )}
                </div>
                <div className="shrink-0 flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
                  <span>{unitCount} unit{unitCount === 1 ? '' : 's'}</span>
                  <span>·</span>
                  <span>{tenantCount} tenant{tenantCount === 1 ? '' : 's'}</span>
                  {group.warningCount > 0 && (
                    <>
                      <span>·</span>
                      <span className="text-amber-600 dark:text-amber-400">{group.warningCount} warning{group.warningCount === 1 ? '' : 's'}</span>
                    </>
                  )}
                  {!hasIssues && (
                    <CheckCircle2 size={16} className="text-emerald-500 shrink-0" aria-label="No issues" />
                  )}
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50">
                  {group.units.length >= VIRTUALIZE_UNIT_LIST_ABOVE ? (
                    <VirtualUnitList
                      units={group.units}
                      editable={editable}
                      onUpdateRent={editable ? handleUpdateUnitRent : undefined}
                      onDismissException={editable ? handleDismissExceptionByRef : undefined}
                    />
                  ) : (
                    <div
                      className="overflow-y-auto p-3 space-y-2 overscroll-contain"
                      style={{ maxHeight: UNIT_LIST_MAX_HEIGHT }}
                      role="list"
                    >
                      {group.units.map((item) => (
                        <UnitRowContent
                          key={item.unitIndex}
                          unit={item.unit}
                          tenant={item.tenant}
                          lease={item.lease}
                          exceptions={item.exceptions}
                          isOnlyUnit={group.units.length === 1}
                          unitIndex={item.unitIndex}
                          editable={editable}
                          onUpdateRent={editable ? (val) => handleUpdateUnitRent(item.unitIndex, val) : undefined}
                          onDismissException={editable ? handleDismissExceptionByRef : undefined}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
            })}
          </div>

          {showPagination && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button
                type="button"
                disabled={accordionPage === 0}
                onClick={() => setAccordionPage((p) => p - 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm disabled:opacity-50"
              >
                Previous
              </button>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                Page {accordionPage + 1} of {totalPages}
              </span>
              <button
                type="button"
                disabled={accordionPage >= totalPages - 1}
                onClick={() => setAccordionPage((p) => p + 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-sm disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}

          <div className="flex justify-between gap-2 pt-2">
            <button type="button" onClick={() => setReviewStep(2)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium">Back</button>
            <button type="button" onClick={() => setReviewStep(4)} className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium">Next</button>
          </div>
        </div>
      )}

      {reviewStep === 4 && (
        <div className="space-y-6">
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-5 space-y-3">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Creating</p>
            <p className="text-slate-600 dark:text-slate-400">
              {totalProps} properties · {totalUnits} units · {totalTenants} tenants · {totalLeases} leases
            </p>
            <p className="text-slate-600 dark:text-slate-400">
              {totalDeposits} security deposits · {ownerCount} owner{ownerCount === 1 ? '' : 's'} · {investorCount} investor{investorCount === 1 ? '' : 's'}
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-amber-50/50 dark:bg-amber-900/20 p-5">
            <p className="text-sm font-medium text-amber-800 dark:text-amber-200">Warnings</p>
            <p className="text-slate-600 dark:text-slate-400 mt-1">
              {totalWarnings} item{totalWarnings === 1 ? '' : 's'} flagged — these will be imported and visible in your exception queue after signup.
            </p>
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-5">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Skipped</p>
            <p className="text-slate-600 dark:text-slate-400 mt-1">0 rows</p>
          </div>
          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmedFlagged}
              onChange={(e) => setConfirmedFlagged(e.target.checked)}
              className="rounded border-slate-300 dark:border-slate-600 mt-0.5"
            />
            <span className="text-sm text-slate-700 dark:text-slate-300">I understand flagged items will need review after signup.</span>
          </label>
          <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50/50 dark:bg-slate-800/30 p-5">
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Your first steps after signup</p>
            <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-1 list-disc list-inside">
              <li>Invite tenant portals</li>
              <li>Review exceptions in your queue</li>
              <li>Complete property operations setup where needed</li>
            </ul>
          </div>
          <div className="flex justify-between gap-2">
            <button type="button" onClick={() => setReviewStep(3)} className="px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-sm font-medium">Back</button>
            <button
              type="button"
              onClick={handleContinueToAccount}
              disabled={!confirmedFlagged}
              className="px-4 py-2 rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Continue to account creation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
